/**
 * SIX DOTS — the mark that says "take hold of this".
 *
 * It lives on its own because two unrelated files draw it: the divider between
 * the stage and the minutes (`ResizeHandle`), and the fold button straddling the
 * bottom of the run bar (`RunBar`). Those two already claimed in their comments
 * to be "the same shape", and were not — one was a 6×48 pill, the other a 3×24
 * bar, and each drifted whenever the other was tuned. Same mark, one file, so
 * the claim can stay true.
 *
 * WHY DOTS AT ALL. A pill is a shape; the dot grid is the sign people already
 * read as "pick this up", and it is what the link builder's draggable rows wear
 * (SitePageDock's ⠿). Every gesture in this app that means "grab and move" now
 * looks like the others.
 *
 * 2px dots on a 2px pitch: 6px across the cluster and 10px along it. That is
 * what the horizontal divider has room for, and findability does not rest on it —
 * the divider's grab band is 16px whatever the mark looks like, and the run bar's
 * fold button is an invisible 16×56 behind its dots.
 */

export function GrabDots({
  columns = 2,
  dotClassName,
  className = '',
}: {
  /** 2 stands the grip UP — two dots across, three along. 3 lays it on its side.
   *
   *  It follows the EDGE, not the gesture: three along a vertical divider, three
   *  across a horizontal one, and across for the run bar's fold button, which is
   *  a click rather than a drag but still sits on a horizontal edge. A standing
   *  grip on a lying edge is the one combination that reads as a mistake. */
  columns?: 2 | 3;
  /** The dots' colour, including hover and active states, which differ by host
   *  (the divider lights indigo on drag; the button on hover). */
  dotClassName: string;
  /** Placement only. Both hosts position this absolutely. */
  className?: string;
}) {
  return (
    <div
      data-testid="grab-dots"
      // w-max is load-bearing. The column divider is a 1px element, so an
      // absolutely-positioned grid inside it gets 1px of available width and
      // `grid-cols-N` — repeat(N, minmax(0, 1fr)) — happily shrinks its tracks to
      // zero. The columns then stack on top of each other and six dots render as
      // three. Sizing to max-content is what keeps them apart.
      className={`grid w-max gap-[2px] ${
        columns === 3 ? 'grid-cols-3' : 'grid-cols-2'
      } ${className}`}
    >
      {Array.from({ length: 6 }, (_, i) => (
        <span
          key={i}
          className={`h-[2px] w-[2px] rounded-full transition-colors ${dotClassName}`}
        />
      ))}
    </div>
  );
}
